import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithPhoneNumber,
  signInWithPopup,
  signInWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { auth, isFirebaseConfigured } from '../firebase';
import { initFcmForUser, removeTokenForUser } from '../services/fcmService';

const AuthContext = createContext(null);
const LOCAL_USERS_KEY = 'sm_fitness_local_users';
const LOCAL_SESSION_KEY = 'sm_fitness_local_session';

function readJson(key, fallback) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function writeJson(key, value) {
  window.localStorage.setItem(key, JSON.stringify(value));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!auth || !isFirebaseConfigured) {
      const session = readJson(LOCAL_SESSION_KEY, null);
      setUser(session);
      setIsReady(true);
      return undefined;
    }

    const unsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser && firebaseUser.emailVerified) {
        setUser({
          id: firebaseUser.uid,
          name: firebaseUser.displayName || '',
          email: firebaseUser.email || '',
          phone: firebaseUser.phoneNumber || '',
        });
        // Silently refresh FCM token on session restore
        initFcmForUser(firebaseUser.uid).catch(() => {});
      } else {
        setUser(null);
      }
      setIsReady(true);
    });
    return () => unsub();
  }, []);

  const login = async (email, password) => {
    if (!auth || !isFirebaseConfigured) {
      const normalizedEmail = String(email || '').trim().toLowerCase();
      const cleanPassword = String(password || '');
      const users = readJson(LOCAL_USERS_KEY, []);
      const matched = users.find(
        (item) =>
          String(item.email || '').toLowerCase() === normalizedEmail &&
          String(item.password || '') === cleanPassword
      );

      if (!matched) {
        return { ok: false, message: 'Invalid email or password.' };
      }

      const localUser = { id: matched.id, name: matched.name, email: matched.email };
      writeJson(LOCAL_SESSION_KEY, localUser);
      setUser(localUser);
      return { ok: true };
    }

    try {
      const credential = await signInWithEmailAndPassword(auth, String(email || '').trim(), String(password || ''));
      if (!credential.user.emailVerified) {
        await signOut(auth);
        return { ok: false, message: 'Please verify your email before signing in. Check your inbox for the verification link.' };
      }
      initFcmForUser(credential.user.uid).catch(() => {});
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (
        code === 'auth/invalid-credential' ||
        code === 'auth/user-not-found' ||
        code === 'auth/wrong-password'
      ) {
        return { ok: false, message: 'Invalid email or password.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      return { ok: false, message: 'Unable to login right now. Please try again.' };
    }
  };

  const register = async ({ name, email, password }) => {
    const cleanName = String(name || '').trim();
    const normalizedEmail = String(email || '').trim();
    const cleanPassword = String(password || '');

    if (!cleanName || !normalizedEmail || !cleanPassword) {
      return { ok: false, message: 'All fields are required.' };
    }
    if (cleanPassword.length < 6) {
      return { ok: false, message: 'Password must be at least 6 characters.' };
    }

    if (!auth || !isFirebaseConfigured) {
      const users = readJson(LOCAL_USERS_KEY, []);
      const emailInUse = users.some(
        (item) => String(item.email || '').toLowerCase() === normalizedEmail.toLowerCase()
      );
      if (emailInUse) {
        return { ok: false, message: 'An account with this email already exists.' };
      }

      const localUser = {
        id: `local-${Date.now()}`,
        name: cleanName,
        email: normalizedEmail,
      };
      const localUserWithPassword = { ...localUser, password: cleanPassword };
      writeJson(LOCAL_USERS_KEY, [...users, localUserWithPassword]);
      writeJson(LOCAL_SESSION_KEY, localUser);
      setUser(localUser);
      return { ok: true };
    }

    try {
      const credential = await createUserWithEmailAndPassword(auth, normalizedEmail, cleanPassword);
      await updateProfile(credential.user, { displayName: cleanName });
      await sendEmailVerification(credential.user);
      await signOut(auth);
      return { ok: true, emailVerificationSent: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/email-already-in-use') {
        return { ok: false, message: 'An account with this email already exists.' };
      }
      if (code === 'auth/invalid-email') {
        return { ok: false, message: 'Please enter a valid email address.' };
      }
      if (code === 'auth/weak-password') {
        return { ok: false, message: 'Password is too weak. Use at least 6 characters.' };
      }
      return { ok: false, message: 'Unable to register right now. Please try again.' };
    }
  };

  const logout = async () => {
    if (!auth || !isFirebaseConfigured) {
      window.localStorage.removeItem(LOCAL_SESSION_KEY);
      setUser(null);
      return;
    }
    const uid = auth.currentUser?.uid;
    if (uid) await removeTokenForUser(uid).catch(() => {});
    await signOut(auth);
  };

  const loginWithGoogle = async () => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Google login requires Firebase configuration.' };
    }
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      initFcmForUser(result.user.uid).catch(() => {});
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/popup-closed-by-user') {
        return { ok: false, message: 'Google login was cancelled.' };
      }
      return { ok: false, message: 'Unable to login with Google right now.' };
    }
  };

  const sendPhoneOTP = async (phoneNumber, appVerifier) => {
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Phone login requires Firebase configuration.' };
    }
    try {
      const confirmationResult = await signInWithPhoneNumber(auth, phoneNumber, appVerifier);
      return { ok: true, confirmationResult };
    } catch (error) {
      const code = error?.code || '';
      console.error('[Phone OTP error]', code, error?.message);
      if (code === 'auth/invalid-phone-number') {
        return { ok: false, message: 'Invalid phone number. Use international format (+1234567890).' };
      }
      if (code === 'auth/too-many-requests') {
        return { ok: false, message: 'Too many attempts. Please try again later.' };
      }
      if (code === 'auth/operation-not-allowed') {
        return { ok: false, message: 'Phone authentication is not enabled for this project.' };
      }
      if (code === 'auth/quota-exceeded') {
        return { ok: false, message: 'SMS quota exceeded. Please try again later.' };
      }
      if (code === 'auth/missing-phone-number') {
        return { ok: false, message: 'Please enter a phone number.' };
      }
      if (code === 'auth/billing-not-enabled') {
        return { ok: false, message: 'Phone login requires a Firebase Blaze plan. Please upgrade your Firebase project billing.' };
      }
      return { ok: false, message: `Unable to send OTP (${code || 'unknown'}). Please try again.` };
    }
  };

  const resetPassword = async (email) => {
    const normalizedEmail = String(email || '').trim();
    if (!normalizedEmail) {
      return { ok: false, message: 'Please enter your email address.' };
    }
    if (!auth || !isFirebaseConfigured) {
      return { ok: false, message: 'Password reset requires Firebase configuration.' };
    }
    try {
      await sendPasswordResetEmail(auth, normalizedEmail);
      return { ok: true };
    } catch (error) {
      const code = error?.code || '';
      if (code === 'auth/user-not-found' || code === 'auth/invalid-email') {
        // Don't reveal whether email exists — generic success message
        return { ok: true };
      }
      if (code === 'auth/too-many-requests') {
        return { ok: false, message: 'Too many requests. Please try again later.' };
      }
      return { ok: false, message: 'Unable to send reset email. Please try again.' };
    }
  };

  const verifyPhoneOTP = async (confirmationResult, code) => {
    try {
      await confirmationResult.confirm(code);
      return { ok: true };
    } catch (error) {
      const errCode = error?.code || '';
      if (errCode === 'auth/invalid-verification-code') {
        return { ok: false, message: 'Invalid code. Please check and try again.' };
      }
      if (errCode === 'auth/code-expired') {
        return { ok: false, message: 'Code expired. Please request a new one.' };
      }
      return { ok: false, message: 'Unable to verify code. Please try again.' };
    }
  };

  const value = {
    user,
    isReady,
    isAuthenticated: Boolean(user),
    login,
    register,
    logout,
    loginWithGoogle,
    resetPassword,
    sendPhoneOTP,
    verifyPhoneOTP,
    isFirebaseConfigured,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return ctx;
}
